import React, { useState } from "react";
import { Auth } from "shared/@auth/Auth";
import { useMount, useUnmount } from "react-use";
import TabActionBar from "shared/TabActionBar";
import Button from "shared/Button";
import styles from "./index.module.css";
import formatDate from "shared/@utils/formatDate";
import { descendingBy } from "shared/@utils/sort";
import { RECENT_EVENTS_IBOS_KEY } from "../OrdersByIBOLegacy/index";
import moment from "moment-timezone";

import { Orders } from "../OrdersByIBOLegacy/useOrders";
import { ENVIRONMENT_CONFIG } from "shared/globals";
import RefundOrderConfirmationModal from "Events/Components/RefundOrderConfirmationModal";
import RefundConfirmationModal from "Events/Components/RefundConfirmationModal";
import SpecialNeedsModal from "Events/Components/SpecialNeedsModal";
import { useTicketSpecialNeeds } from "./useTicketSpecialNeeds";
import Loader from "shared/Loader";
import { useEffect } from "react";
import { useQueryClient } from "react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWheelchair, faQrcode } from "@fortawesome/free-solid-svg-icons";
import { useRefundOrder } from "./useRefundOrder";
import { useRefundTicket } from "./useRefundTicket";
import { useLocalTicketCancelPut } from "./useLocalTicketCancelPut";
import { Modal } from "shared/Modal";
import NoteField from "../NoteField";

function appendToRecents(iboNumber, name) {
  const recentEventIbos = JSON.parse(
    sessionStorage.getItem(RECENT_EVENTS_IBOS_KEY) || "[]"
  );

  // Keep the latest 9 results.
  let newList = recentEventIbos.filter((item) => item.iboNumber !== iboNumber);
  newList.sort(descendingBy("timestamp"));
  newList = newList.slice(0, 9);

  // Prepend this one.
  newList.unshift({
    iboNumber,
    name,
    timestamp: new Date(),
  });

  sessionStorage.setItem(RECENT_EVENTS_IBOS_KEY, JSON.stringify(newList));
}

export default function Order({ iboInfo, order }) {
  const isLocalFunction = order.orderInfo.eventName == "LOCAL FUNCTIONS";
  const { refreshIBOInfo, updateNote } = Orders.useContainer();
  const [applyFee, setApplyFee] = useState(false);
  const updateTicketAdaValue = (ticketId, newAdaValue) => {
    const updatedOrderData = {
      ...orderData,
      events: orderData.events.map((event) => ({
        ...event,
        tickets: event.tickets.map((ticket) =>
          ticket.ticketId === ticketId
            ? { ...ticket, adaValue: newAdaValue }
            : ticket
        ),
      })),
    };

    setOrderData(updatedOrderData);
  };
  const { iboNumber, displayName } = iboInfo;

  const { hasPermission } = Auth.useContainer();

  useMount(() => {
    appendToRecents(iboNumber, displayName);
  });

  useUnmount(() => {
    appendToRecents(iboNumber, displayName);
  });

  const { revokeOrder, orders } = Orders.useContainer();
  const { orderInfo, events, payments } = order;
  const users = orders.users;
  const [noteInitialText, setNoteInitialText] = useState(orderInfo.notes);
  useEffect(() => {
    setNoteInitialText(orderInfo.notes ?? "");
  }, [order]);

  const saveNote = async (noteText) => {
    const orderId = orderInfo.orderId;
    const noteData = { notes: noteText };
    try {
      setIsLoading(true);
      await updateNote
        .mutateAsync({ orderId, noteData })
        .then(() => refreshIBOInfo().then(() => setIsLoading(false)));
    } catch (error) {
      console.error("Error updating note: ", error);
    }
  };

  const [isOpen, setIsOpen] = useState(false);

  function onRevokeOrder() {
    let orderToRevoke = {
      orderId: orderInfo.orderId,
    };

    revokeOrder.mutate(orderToRevoke);
    orderInfo.status = "REVOKED";

    events.forEach((event) => {
      event.tickets.forEach((ticket) => {
        if (ticket.statusId === 1) {
          ticket.statusId = 4;
        }
      });
    });
  }

  const currentYear = formatDate(new Date(), "YY");
  const createdOnYear = formatDate(orderInfo.createdOn, "YY");

  let canRevoke =
    currentYear === createdOnYear && orderInfo.status === "COMPLETED";
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingModals, setIsLoadingModals] = useState(false);
  const [refundCompleted, setRefundCompleted] = useState(false);
  const [refundMessage, setRefundMessage] = useState("");
  const [refundMessageType, setRefundMessageType] = useState("success"); // 'error' as another possible value
  const { mutateAsync: refundOrder } = useRefundOrder();

  const onRefundOrder = async (applyFee) => {
    setIsLoadingModals(true);

    try {
      let refundAmount = 0;
      order.events[0].tickets.forEach((ticket) => {
        if (ticket.statusId === 1) {
          refundAmount += ticket.cost + ticket.tax;
        }
      });

      let tickets = [];
      order.events[0].tickets.forEach((ticket) => {
        if (ticket.statusId === 1) {
          tickets.push({
            ticketId: ticket.ticketId,
            amountPaid: ticket.cost + ticket.tax,
          });
        }
      });
      const refundData = {
        iboNumber: iboInfo.iboNumber,
        orderId: orderInfo.orderId,
        year: orderInfo.year,
        amountPaid:
          order.payments && order.payments[0] && order.payments[0].amount
            ? order.payments[0].amount
            : 0,
        ccId:
          order.payments && order.payments[0] && order.payments[0].ccId
            ? order.payments[0].ccId
            : "",
        amountRefunding: refundAmount,
        tickets: tickets,
        addFee: applyFee,
      };
      const { message, status } = await refundOrder(refundData);

      if (status === 204 || status === 200) {
        setRefundMessage(message);
        setRefundMessageType("success");
      } else {
        setRefundMessage(message);
        setRefundMessageType("error");
      }
      setRefundCompleted(true);
    } catch (error) {
      setRefundMessage("An unexpected error occurred.");
      setRefundMessageType("error");
    } finally {
      // await refreshIBOInfo();
      setIsLoadingModals(false);
    }
  };
  const [orderData, setOrderData] = useState(order);
  // useEffect(() => {
  //   // This effect will run whenever the `order` prop changes.
  //   setOrderData(order);
  // }, [order]);
  const handleCloseModal = () => {
    setIsOpen(false);
    if (refundMessage) {
      setIsLoading(true);
      setRefundCompleted(false);
      setRefundMessage("");
      setRefundMessageType("success");
      refreshIBOInfo().then(() => setIsLoading(false));
      // window.location.reload();
    }
  };

  const getCancelOrRefund = () => {
    let totalAmount = 0;
    // totalAmount = order.payments.reduce(
    //   (total, payment) => total + payment.amount,
    //   0
    // );
    totalAmount += order.events.reduce((totalEvent, event) => {
      return (
        totalEvent +
        event.tickets.reduce((totalTicket, ticket) => {
          return ticket.statusId === 1
            ? totalTicket + (ticket.cost + ticket.tax)
            : totalTicket;
        }, 0)
      );
    }, 0);

    return totalAmount <= 0 ? 0 : 1;
  };

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <div className={styles.sectionTopMargin}>
            <TabActionBar
              style={{
                justifyContent: "space-between",
              }}
            >
              <div className={styles.headerText}>
                {orderInfo.eventName} {orderInfo.year}
              </div>
              {canRevoke && (
                <div className={styles.headerButtonGroup}>
                  <Button
                    onClick={() => {
                      onRevokeOrder();
                    }}
                  >
                    Revoke
                  </Button>
                  {!isLocalFunction && (
                    <div className={styles.refundContainer}>
                      <Button
                        className={styles["refund-button"]}
                        onClick={() => {
                          setIsOpen(!isOpen);
                        }}
                      >
                        {getCancelOrRefund() == 1
                          ? "Refund Order"
                          : "Cancel Order"}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </TabActionBar>
            <div className={styles.containerOrderConf}>
              <div className={styles.orderConf}>
                <span className={styles.orderConfTitle}>
                  Order Confirmation
                </span>
                <span className={styles.confirmationCode}>
                  Confirmation Code: {iboNumber}-{orderInfo.orderId}
                </span>
                <span className={styles.purchaseDate}>
                  Purchased: {formatDate(orderInfo.createdOn)}
                </span>
                <span>Order Status: {orderInfo.status}</span>

                {!isLocalFunction && (
                  <div className={styles.noteArea}>
                    <NoteField
                      initialText={noteInitialText}
                      onSave={(noteText) => saveNote(noteText)}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="padding-top border-bottom"></div>
            {events &&
              events.map((event, index) => {
                return (
                  <div className={styles.containerTicketSum} key={index}>
                    <div className={styles.ticketSummary}>
                      <div className={styles.containerEvent}>
                        <div className={styles.containereventId}>
                          {/* Event Name */}
                          <span className={styles.eventId}>
                            {event.state
                              ? `${event.city}, ${event.state}`
                              : event.city}
                          </span>
                        </div>
                        {event.city !== "PURCHASE CREDITS" && (
                          <>
                            <div className={styles.containerstartsOn}>
                              {/* Start Date */}
                              <span className={styles.startsOn}>
                                {formatDate(event.startsOn, "MM.DD.YY")}
                              </span>
                            </div>
                            <div className={styles.containerDateSeparator}>
                              {/* Date Seperator */}
                              <span className={styles.dateSeparator}>
                                &nbsp;-&nbsp;
                              </span>
                            </div>
                            <div className={styles.containerendsOn}>
                              {/* End Date */}
                              <span className={styles.endsOn}>
                                {formatDate(event.endsOn, "MM.DD.YY")}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                      <div className={styles.containerTickets}>
                        {event.tickets.map((ticket, ticketId) => {
                          let ticketColor = styles.ticketActive;
                          if (ticket.statusId === 2 || ticket.statusId === 3) {
                            ticketColor = styles.ticketRefunded;
                          } else if (ticket.statusId === 4) {
                            ticketColor = styles.ticketRevoked;
                          }

                          return (
                            <div
                              key={ticketId}
                              className={styles.ticket + " " + ticketColor}
                            >
                              <div className={styles.left}>
                                {/* Ticket > ID */}
                                <div className={styles.ticketId}>
                                  Ticket #{ticket.ticketId}
                                </div>
                                {/* Ticket > Price */}
                                <div className={styles.ticketPrice}>
                                  ${ticket.cost.toFixed(2)}{" "}
                                  {ticket.tax !== 0
                                    ? `+ $${ticket.tax.toFixed(2)} Tax`
                                    : ""}
                                </div>
                                {/* Ticket Type */}
                                <div className={styles.ticketType}>
                                  {!isLocalFunction && ticket.priceTypeId === 5
                                    ? "Fee"
                                    : ""}
                                  {ticket.priceType}
                                  {isLocalFunction && (
                                    <div className={styles.divide} />
                                  )}
                                  {/* {console.log(
                                    ticket.priceType,
                                    ticket.statusId,
                                    ticket
                                  )} */}
                                  {ticket.statusId === 2 && (
                                    <span> *** CANCELLED *** </span>
                                  )}
                                  {ticket.statusId === 3 && (
                                    <span> *** EXPIRED *** </span>
                                  )}
                                  {ticket.statusId === 4 && (
                                    <span> *** REVOKED *** </span>
                                  )}
                                </div>
                              </div>
                              <div className={styles.right}>
                                {ticket.statusId === 1 &&
                                  (isLocalFunction ||
                                    ticket.priceTypeId !== 5) && (
                                    <CheckInPrint
                                      ticket={ticket}
                                      order={order}
                                      iboInfo={iboInfo}
                                      isLoading={isLoading}
                                      setIsLoading={setIsLoading}
                                    ></CheckInPrint>
                                  )}
                                {/* <span></span> */}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            <div className="padding-top border-bottom"></div>
            {payments.map((payment, ctr) => {
              return (
                <div className={styles.containerPaymentConf}>
                  <div className={styles.paymentConf}>
                    <span className={styles.paymentConfTitle}>
                      Transaction #{ctr + 1}
                    </span>
                    <span className={styles.paymentConfTitle}>
                      {payment.nickname}
                    </span>
                    <span className={styles.amountPaid}>
                      Amount Paid:{" "}
                      <span className={styles.amountPaidValue}>
                        ${payment.amount.toFixed(2)}
                      </span>
                    </span>
                    <span className={styles.paymentMethod}>
                      {payment.cardDisplay}
                    </span>
                    <span className={styles.paymentMethod}>
                      Status: {payment.status}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="padding-top border-bottom"></div>

          <RefundOrderConfirmationModal
            isOpen={isOpen}
            onCollapse={() => {
              setIsOpen(!isOpen);
              setIsLoading(true);
              setRefundCompleted(false);
              setRefundMessage("");
              setRefundMessageType("success");
              refreshIBOInfo().then(() => setIsLoading(false));
            }}
            onClose={handleCloseModal}
            onClick={onRefundOrder}
            isLoading={isLoadingModals}
            refundCompleted={refundCompleted}
            applyFee={applyFee}
            setApplyFee={setApplyFee}
            refundMessage={refundMessage}
            refundMessageType={refundMessageType}
            titleType={getCancelOrRefund()}
          />
        </>
      )}
    </>
  );
}

function CheckInPrint({ ticket, order, iboInfo, isLoading, setIsLoading }) {
  const isLocalFunction = order.orderInfo.eventName == "LOCAL FUNCTIONS";
  const { orders, checkIn, uncheckTicket, refreshIBOInfo } =
    Orders.useContainer();
  const [contactId, setContactId] = useState(ticket.contactId);
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  const [showADAModal, setShowADAModal] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { hasPermission } = Auth.useContainer();
  const [refundCompleted, setRefundCompleted] = useState(false);
  const [refundMessage, setRefundMessage] = useState("");
  const [refundMessageType, setRefundMessageType] = useState("success");
  const [applyFee, setApplyFee] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [ticketScans, setTicketScans] = useState(ticket.scans ?? []);
  useEffect(() => {
    if (ticket) {
      setTicketScans(ticket.scans);
      setContactId(ticket.contactId);
    }
  }, [order, ticket, orders]);

  const users = orders.users;
  const { mutateAsync: refundTicket } = useRefundTicket();
  const { mutateAsync: cancelTicketMutation } = useLocalTicketCancelPut();

  const tickets = orders.orders
    .filter((a) => a.orderInfo.orderId == order.orderInfo.orderId)
    .flatMap((a) => a.events)
    .filter((a) => a.eventId == ticket.eventId)
    .flatMap((a) => a.tickets);

  function onCheckInClicked(u) {
    checkIn
      .mutateAsync({
        ticketId: ticket.ticketId,
        contactId: u.userId,
      })
      .then(() => {
        setContactId(u.contactId);
        setIsCheckingIn(false);
      });
  }

  function shouldShowContact(u) {
    return (
      tickets
        .filter((a) => a.contactId != null)
        .map((a) => a.contactId)
        .indexOf(u.contactId) < 0
    );
  }

  // function printTicket() {
  //   let base64 = new Buffer(ticket.token).toString("base64");
  //   let url = coreApiUrl + `/v1/event/print/ticket/${base64}`;
  //   window.open(url, "_self");
  // }

  function onUncheckTicketClicked() {
    uncheckTicket
      .mutateAsync({
        ticketId: ticket.ticketId,
        contactId: null,
      })
      .then(() => {
        setContactId(null);
      });
  }

  function checkedInName(t) {
    const ticket = orders.orders
      .filter((a) => a.orderInfo.orderId == order.orderInfo.orderId)
      .flatMap((a) => a.events)
      .filter((a) => a.eventId == t.eventId)
      .flatMap((a) => a.tickets)
      .find((a) => a.ticketId == t.ticketId);
    let u = ticket.contactId
      ? users.find((u) => u.userId == ticket.contactId)
      : null;
    if (u) {
      return u.strFirst + " " + u.strLast;
    }
    return "";
  }

  const onRefundTicket = async (applyFee) => {
    setIsLoadingModals(true);

    try {
      if (ticket.priceType == "REDEEM CREDITS" && isLocalFunction) {
        const refundData = {
          orderId: order.orderInfo.orderId,
          ticketId: ticket.ticketId,
        };

        const { message, status } = await cancelTicketMutation(refundData);

        if (status === 204 || status === 200) {
          setRefundMessage(message);
          setRefundMessageType("success");
        } else {
          setRefundMessage(message);
          setRefundMessageType("error");
        }
        setRefundCompleted(true);
      } else {
        const refundData = {
          ruleset: order.orderInfo.ruleset,
          iboNumber: iboInfo.iboNumber,
          orderId: order.orderInfo.orderId,
          year: order.orderInfo.year,
          amountPaid:
            order.payments && order.payments[0] && order.payments[0].amount
              ? order.payments[0].amount
              : 0,
          amountRefunding: ticket.cost + ticket.tax,
          ccId:
            order.payments && order.payments[0] && order.payments[0].ccId
              ? order.payments[0].ccId
              : "",
          ticketId: ticket.ticketId,
          addFee: applyFee,
        };

        const { message, status } = await refundTicket(refundData);

        if (status === 204 || status === 200) {
          setRefundMessage(message);
          setRefundMessageType("success");
        } else {
          setRefundMessage(message);
          setRefundMessageType("error");
        }
        setRefundCompleted(true);
      }
    } catch (error) {
      setRefundMessage("An unexpected error occurred.");
      setRefundMessageType("error");
    } finally {
      // await refreshIBOInfo();
      setIsLoadingModals(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    if (refundMessage) {
      setIsLoading(true);
      setRefundCompleted(false);
      setRefundMessage("");
      setRefundMessageType("success");
      refreshIBOInfo().then(() => setIsLoading(false));
    }
  };

  const [isScanModalOpen, setIsScanModalOpen] = useState(false);
  const [isSpecialNeedsModalOpen, setIsSpecialNeedsModalOpen] = useState(false);
  const handleQRCodeClick = () => {
    setIsScanModalOpen(true);
  };
  const handleAddSpecialNeedsClick = () => {
    setIsSpecialNeedsModalOpen(true);
  };
  const [specialNeedsMessage, setSpecialNeedsMessage] = useState(null);

  const { updateSpecialNeedsMutation } = useTicketSpecialNeeds();
  const [isLoadingModals, setIsLoadingModals] = useState(false);

  const [messageType, setMessageType] = useState("success"); // 'error' as another possible value
  const queryClient = useQueryClient();

  useEffect(() => {
    const refetchData = async () => {
      await queryClient.refetchQueries(
        `v1/events/view/events/sale/ibo/${iboInfo.iboNumber}`
      );
    };

    // Call refetchData function after closing the modal
    if (!isSpecialNeedsModalOpen) {
      refetchData();
    }
  }, [isSpecialNeedsModalOpen, iboInfo.iboNumber, queryClient]);

  const handleSpecialNeedsSave = async (updatedTicket) => {
    setSpecialNeedsMessage("");
    setIsLoadingModals(true);
    try {
      const response = await updateSpecialNeedsMutation.mutateAsync({
        ticketId: updatedTicket.ticketId,
        adaValue: arrayToBitwise(updatedTicket.adaValues),
      });

      if (response.status === 200) {
        setSpecialNeedsMessage("Special needs successfully updated.");
        setMessageType("success");
      } else if (response.status === 409) {
        setSpecialNeedsMessage(
          "Conflict: Your changes conflict with existing data."
        );
        setMessageType("error");
      } else {
        setSpecialNeedsMessage(
          "Error updating special needs. Please try again later."
        );
        setMessageType("error");
      }
    } catch (error) {
      setSpecialNeedsMessage(
        "Error updating special needs. Please try again later."
      );
      setMessageType("error");
      console.error("Error updating special needs:", error);
    } finally {
      await refreshIBOInfo();
      setIsLoadingModals(false);
    }
  };

  const handleSpecialNeedsCancel = () => {
    setSpecialNeedsMessage("");
    setIsSpecialNeedsModalOpen(false);
  };

  function bitwiseToArray(bitwise) {
    if (bitwise == null || bitwise == 0) {
      return [];
    }

    const bitwiseValues = [
      32768, // Accessible Seating
      65536, // Accessible Seating Companion Ticket
      4, // Hearing Amplification Device
      8, // American Sign Language Interpreter
      131072, // Hearing Accessible Seating Companion Ticket
      262144, // Nursing Mother
    ];

    return bitwiseValues.filter((value) => (bitwise & value) !== 0);
  }

  function arrayToBitwise(valuesArray) {
    return valuesArray.reduce((bitwise, value) => bitwise | value, 0);
  }

  return (
    <>
      {/* {console.log(contactId)} */}
      {contactId != null && (
        <div className={styles.buttonGroups}>
          {checkedInName(ticket)}
          &nbsp;
          <Button onClick={handleAddSpecialNeedsClick}>
            <FontAwesomeIcon icon={faWheelchair} />
          </Button>
          &nbsp;
          <Button onClick={handleQRCodeClick}>
            <FontAwesomeIcon icon={faQrcode} />
          </Button>
          &nbsp;
          <Button onClick={() => onUncheckTicketClicked()}>Un-Check</Button>
          &nbsp;
          {/*  <Button onClick={() => printTicket()}>Print</Button> */}
        </div>
      )}
      {contactId == null && (
        <div className={styles["dropleft"]}>
          <div
            className={
              styles["dropdown-menu"] + " " + (isCheckingIn && styles["show"])
            }
          >
            {users.map((a, userId) => {
              if (shouldShowContact(a)) {
                return (
                  <a
                    key={a.contactId}
                    className={styles["dropdown-item"]}
                    href="#"
                    onClick={() => {
                      onCheckInClicked(a);
                    }}
                  >
                    {a.strFirst} {a.strLast}
                  </a>
                );
              }
              return <></>;
            })}
          </div>
          <div className={styles["refund-container"]}>
            {/* <Button
              className={styles["ada-button"]}
              onClick={handleAddSpecialNeedsClick}
            >
              ADA
            </Button> */}
            {!isLocalFunction && (
              <Button onClick={handleAddSpecialNeedsClick}>
                <FontAwesomeIcon icon={faWheelchair} />
              </Button>
            )}
            {!isLocalFunction && (
              <Button
                className={styles["dropdown-toggle"]}
                onClick={() => {
                  setIsCheckingIn(!isCheckingIn);
                }}
              >
                Check In
                {/*  */}
              </Button>
            )}
            <Button
              className={styles["refund-button"]}
              onClick={() => {
                setIsModalOpen(!isModalOpen);
              }}
            >
              {!isLocalFunction
                ? ticket.cost <= 0
                  ? "Cancel"
                  : "Refund"
                : ticket.priceType == "REDEEM CREDITS"
                ? "Cancel"
                : "Refund"}
            </Button>
          </div>
        </div>
      )}

      {isScanModalOpen && (
        <div className="modalBackdrop">
          <Modal
            title="Tickets Scanned"
            isOpen={isScanModalOpen}
            onClose={() => setIsScanModalOpen(false)}
            children={
              <>
                <div className={styles["table-container"]}>
                  {ticketScans.length <= 0 ? (
                    <div>There are no scans for this ticket</div>
                  ) : (
                    <>
                      <div className={styles.scansContainer}>
                        {/* {ticketScans.map((scan, index) => {
                          const previousScan = ticketScans[index - 1] || {};

                          // Always render session, location, and station as separate units
                          const isSameSessionLocationStation =
                            previousScan.session === scan.session &&
                            previousScan.location === scan.location &&
                            previousScan.station === scan.station;

                          return (
                            <div key={index} className={styles.scan}>
                              {!isSameSessionLocationStation && (
                                <>
                                  <div className={styles.session}>
                                    {scan.session}
                                  </div>
                                  <div className={styles.location}>
                                    {scan.location}
                                  </div>
                                  <div className={styles.station}>
                                    Station {scan.station}
                                  </div>
                                </>
                              )}

                              <div className={styles.scannedOn}>
                                {moment
                                  .tz(scan.scannedOn, moment.tz.guess())
                                  .format("MM/DD/YYYY h:mm A z")}
                              </div>
                            </div>
                          );
                        })} */}
                        {ticketScans.map((scan, index) => {
                          const previousScan = ticketScans[index - 1] || {};

                          // Display the session only if it's different from the previous scan
                          const displaySession =
                            previousScan.session !== scan.session;

                          // Always display location if it's different from the previous scan OR if it's the same session but a new location
                          const displayLocation =
                            previousScan.location !== scan.location ||
                            displaySession;

                          // Always display station if it's a new station within the same location or a new location
                          const displayStation =
                            previousScan.station !== scan.station ||
                            displayLocation;

                          return (
                            <div key={index} className={styles.scan}>
                              {displaySession && (
                                <div className={styles.session}>
                                  {scan.session}
                                </div>
                              )}

                              {displayLocation && (
                                <div className={styles.location}>
                                  {scan.location}
                                </div>
                              )}

                              {displayStation && (
                                <div className={styles.station}>
                                  Station {scan.station}
                                </div>
                              )}

                              <div className={styles.scannedOn}>
                                {moment
                                  .tz(scan.scannedOn, moment.tz.guess())
                                  .format("MM/DD/YYYY h:mm A z")}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* <table className={styles["custom-table"]}>
                        <thead>
                          <tr>
                            <th>Session</th>
                            <th>Location</th>
                            <th>Station</th>
                            <th>Scanned On</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ticketScans.map((scan, index) => (
                            <tr key={index}>
                              {console.log(scan)}
                              <td>{scan.session}</td>
                              <td>{scan.location}</td>
                              <td>{`Station ${scan.station}`}</td>
                              <td>
                                {moment
                                  .tz(scan.scannedOn, moment.tz.guess())
                                  .format("MM/DD/YYYY h:mm A z")}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table> */}
                    </>
                  )}
                </div>
              </>
            }
          />
        </div>
      )}

      {isSpecialNeedsModalOpen && (
        <div className="modalBackdrop">
          <SpecialNeedsModal
            ticket={{
              ...ticket,
              adaValues: bitwiseToArray(ticket.adaValue),
            }}
            onSave={handleSpecialNeedsSave}
            onCancel={handleSpecialNeedsCancel}
            message={specialNeedsMessage}
            messageType={messageType}
            isLoading={isLoadingModals}
            iboNum={iboInfo.iboNumber}
          />
        </div>
      )}

      <RefundConfirmationModal
        isOpen={isModalOpen}
        onCollapse={() => {
          setIsModalOpen(!isModalOpen);
          setIsLoading(true);
          setRefundCompleted(false);
          setRefundMessage("");
          setRefundMessageType("success");
          refreshIBOInfo().then(() => setIsLoading(false));
        }}
        onClose={handleCloseModal}
        onClick={onRefundTicket}
        isLoading={isLoadingModals}
        refundCompleted={refundCompleted}
        applyFee={applyFee}
        setApplyFee={setApplyFee}
        refundMessage={refundMessage}
        refundMessageType={refundMessageType}
        ticketCost={ticket.cost}
        ticketPriceType={ticket.priceType}
        isLocalFunction={isLocalFunction}
      />

      {/* {isSpecialNeedsModalOpen && (
        <div className="modalBackdrop">
          <SpecialNeedsModal
            ticket={{
              ...ticket,
              adaValues: bitwiseToArray(ticket.adaValue),
            }}
            onSave={handleSpecialNeedsSave}
            onCancel={handleSpecialNeedsCancel}
            message={specialNeedsMessage}
            messageType={messageType}
            isLoading={isLoading}
            iboNum={iboInfo.iboNumber}
          />
        </div>
      )} */}
    </>
  );
}
